import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Activity } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact admin-dashboard widget — last 24h crashes + uptime % + sparkline.
 * Links to /admin/crashes for full RCA view.
 */
export default function CrashUptimeWidget() {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [uptime, setUptime] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [c, u] = await Promise.all([
          axios.get(`${API}/admin/crashes?resolved=false&limit=1`, { headers: getAuthHeader() }),
          axios.get(`${API}/admin/crashes/uptime?hours=24`, { headers: getAuthHeader() }),
        ]);
        if (!alive) return;
        setStats(c.data?.stats || null);
        setUptime(u.data || null);
      } catch {
        // Silent — widget is best-effort
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 60_000); // refresh every minute
    return () => { alive = false; clearInterval(id); };
  }, [getAuthHeader]);

  const uptimePct = uptime?.uptime_pct ?? null;
  const uptimeColor = uptimePct == null
    ? 'text-zinc-500'
    : uptimePct >= 99.9 ? 'text-tiffany-500'
    : uptimePct >= 99 ? 'text-yellow-400'
    : 'text-red-500';
  const last24 = stats?.last_24h ?? 0;
  const unresolved = stats?.unresolved ?? 0;
  const last24Color = last24 === 0 ? 'text-tiffany-500' : last24 < 5 ? 'text-yellow-400' : 'text-red-500';

  // Mini-bar sparkline from uptime samples
  const spark = uptime?.sparkline || [];
  const maxLat = Math.max(1, ...spark.map(s => s.latency || 0));

  return (
    <Link
      to="/admin/crashes"
      className="block border border-zinc-800 hover:border-tiffany-500/40 transition-colors p-5 bg-black"
      data-testid="crash-uptime-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-mono text-sm text-zinc-400 flex items-center gap-2">
          <Activity className="w-4 h-4" />
          STABILITY · 24H
        </h3>
        <span className="font-mono text-[10px] text-zinc-600 tracking-wider">VIEW →</span>
      </div>

      {loading ? (
        <p className="font-mono text-xs text-zinc-600">LOADING_</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className={`font-mono text-2xl font-bold ${uptimeColor}`} data-testid="widget-uptime-pct">
                {uptimePct != null ? `${uptimePct.toFixed(2)}%` : '—'}
              </p>
              <p className="font-mono text-[10px] text-zinc-500 mt-0.5">UPTIME</p>
              {uptime?.avg_latency_ms != null && (
                <p className="font-mono text-[10px] text-zinc-600 mt-0.5">
                  AVG {uptime.avg_latency_ms}ms · P95 {uptime.p95_latency_ms ?? '—'}ms
                </p>
              )}
            </div>
            <div>
              <p className={`font-mono text-2xl font-bold ${last24Color}`} data-testid="widget-crashes-24h">
                {last24}
              </p>
              <p className="font-mono text-[10px] text-zinc-500 mt-0.5">CRASHES</p>
              {unresolved > 0 && (
                <p className="font-mono text-[10px] text-red-400 mt-0.5 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {unresolved} UNRESOLVED
                </p>
              )}
            </div>
          </div>

          {/* Sparkline */}
          {spark.length > 0 && (
            <div className="flex items-end gap-px h-8" data-testid="widget-sparkline">
              {spark.map((s, i) => {
                const h = s.status === 'down' ? '100%' : `${Math.max(8, ((s.latency || 0) / maxLat) * 100)}%`;
                const color = s.status === 'down' ? 'bg-red-500' : (s.latency || 0) > 1000 ? 'bg-yellow-500' : 'bg-tiffany-500/60';
                return <div key={i} className={`flex-1 ${color}`} style={{ height: h }} title={`${new Date(s.t).toLocaleTimeString()} · ${s.latency ?? 0}ms · ${s.status}`} />;
              })}
            </div>
          )}
        </>
      )}
    </Link>
  );
}
